/**
 * Places routes (IA §2): places and visits under Operate › Live (`/live/places`, `/live/visits`,
 * `/places/:id`); assets, beacons and asset rules under Manage (`/fleet/assets`, `/fleet/beacons`,
 * `/fleet/asset-rules`, `/assets/:id`).
 */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';

const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
};

export interface EntitySearch {
  tab?: string;
}
const entitySearch = (s: Record<string, unknown>): EntitySearch => ({ tab: str(s.tab) });

export interface PlacesSearch {
  q?: string;
  kind?: string;
  status?: 'archived' | 'all';
  geofence?: 'on' | 'off';
  tag?: string;
  selected?: string;
  view?: 'map';
  sort?: string;
}

export const placesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live/places',
  staticData: { permission: 'places.place.read' },
  validateSearch: (s: Record<string, unknown>): PlacesSearch => ({
    q: str(s.q),
    kind: str(s.kind),
    status: s.status === 'archived' || s.status === 'all' ? s.status : undefined,
    geofence: s.geofence === 'on' || s.geofence === 'off' ? s.geofence : undefined,
    tag: str(s.tag),
    selected: str(s.selected),
    view: s.view === 'map' ? 'map' : undefined,
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./places/PlacesPage')),
});

export interface NewPlaceSearch {
  lat?: number;
  lon?: number;
  name?: string;
}

export const newPlaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live/places/new',
  staticData: { permission: 'places.place.read' },
  validateSearch: (s: Record<string, unknown>): NewPlaceSearch => ({ lat: num(s.lat), lon: num(s.lon), name: str(s.name) }),
  component: lazyRouteComponent(() => import('./places/NewPlacePage')),
});

export const placeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/places/$placeId',
  staticData: { permission: 'places.place.read' },
  validateSearch: entitySearch,
  component: lazyRouteComponent(() => import('./places/PlacePage')),
});

export interface VisitsSearch {
  q?: string;
  place?: string;
  kind?: string;
  status?: 'ongoing' | 'completed';
  unit?: 'vehicle' | 'asset';
  by?: 'kind' | 'vehicle' | 'asset';
  sort?: string;
}

export const visitsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live/visits',
  staticData: { permission: 'places.visit.read' },
  validateSearch: (s: Record<string, unknown>): VisitsSearch => ({
    q: str(s.q),
    place: str(s.place),
    kind: str(s.kind),
    status: s.status === 'ongoing' || s.status === 'completed' ? s.status : undefined,
    unit: s.unit === 'vehicle' || s.unit === 'asset' ? s.unit : undefined,
    by: s.by === 'kind' || s.by === 'vehicle' || s.by === 'asset' ? s.by : undefined,
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./visits/VisitsPage')),
});

export interface AssetsSearch {
  q?: string;
  status?: 'inactive' | 'all';
  type?: string;
  division?: string;
  tag?: string;
  selected?: string;
  sort?: string;
}

export const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/assets',
  staticData: { permission: 'places.asset.read' },
  validateSearch: (s: Record<string, unknown>): AssetsSearch => ({
    q: str(s.q),
    status: s.status === 'inactive' || s.status === 'all' ? s.status : undefined,
    type: str(s.type),
    division: str(s.division),
    tag: str(s.tag),
    selected: str(s.selected),
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./assets/AssetsPage')),
});

export const assetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets/$assetId',
  staticData: { permission: 'places.asset.read' },
  validateSearch: entitySearch,
  component: lazyRouteComponent(() => import('./assets/AssetPage')),
});

export interface BeaconsSearch {
  q?: string;
  kind?: 'asset_tag' | 'driver_keyfob' | 'sensor';
  linked?: 'yes' | 'no';
  status?: 'inactive' | 'all';
  selected?: string;
  sort?: string;
}

export const beaconsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/beacons',
  staticData: { permission: 'places.beacon.read' },
  validateSearch: (s: Record<string, unknown>): BeaconsSearch => ({
    q: str(s.q),
    kind: s.kind === 'asset_tag' || s.kind === 'driver_keyfob' || s.kind === 'sensor' ? s.kind : undefined,
    linked: s.linked === 'yes' || s.linked === 'no' ? s.linked : undefined,
    status: s.status === 'inactive' || s.status === 'all' ? s.status : undefined,
    selected: str(s.selected),
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./beacons/BeaconsPage')),
});

export interface RulesSearch {
  kind?: 'threshold' | 'geofence' | 'time_window' | 'connectivity';
  enabled?: 'yes' | 'no';
  status?: 'archived' | 'all';
  templates?: '1';
  sort?: string;
}

export const rulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/asset-rules',
  staticData: { permission: 'places.rule.read' },
  validateSearch: (s: Record<string, unknown>): RulesSearch => ({
    kind: s.kind === 'threshold' || s.kind === 'geofence' || s.kind === 'time_window' || s.kind === 'connectivity' ? s.kind : undefined,
    enabled: s.enabled === 'yes' || s.enabled === 'no' ? s.enabled : undefined,
    status: s.status === 'archived' || s.status === 'all' ? s.status : undefined,
    templates: s.templates === '1' || s.templates === 1 ? '1' : undefined,
    sort: str(s.sort),
  }),
  component: lazyRouteComponent(() => import('./rules/RulesPage')),
});

export interface NewRuleSearch {
  template?: string;
  asset?: string;
  place?: string;
}

export const newRuleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/asset-rules/new',
  staticData: { permission: 'places.rule.read' },
  validateSearch: (s: Record<string, unknown>): NewRuleSearch => ({ template: str(s.template), asset: str(s.asset), place: str(s.place) }),
  component: lazyRouteComponent(() => import('./rules/NewRulePage')),
});

export const ruleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/asset-rules/$ruleId',
  staticData: { permission: 'places.rule.read' },
  validateSearch: entitySearch,
  component: lazyRouteComponent(() => import('./rules/RulePage')),
});
